import React, { useEffect, useState, useRef } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import logo from "../assets/logo1.png";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import { Popover, Transition } from "@headlessui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoIosArrowDown } from "react-icons/io";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { FaRegUser } from "react-icons/fa";
import {
  SESSION_IS_AUTHENTICATED,
  SESSION_USERINFO,
} from "../Utills/Constants";
import { TbLayoutDashboardFilled } from "react-icons/tb";
import { PiHospitalFill } from "react-icons/pi";
import { FaStore } from "react-icons/fa";
import { MdOutlineCalendarMonth } from "react-icons/md";

const drawerWidth = 240;

const HOSPITAL_LINKS = [
  { label: "Register Users", path: "/admin/hospital/register" },
  { label: "Manage Staff", path: "/admin/hospital/staff" },
  { label: "Attendance", path: "/admin/hospital/attendance" },
  { label: "Manage Doctors", path: "/admin/hospital/doctor" },
  { label: "Doctors Income", path: "/admin/hospital/doctorincome" },
  {
    label: "Sales By Receptionist",
    path: "/admin/hospital/salebyreceptionist",
  },
  { label: "Patients", path: "/admin/hospital/patients" },
  { label: "Medicines", path: "/admin/hospital/medicines" },
  { label: "Profit Loss Head", path: "/admin/hospital/profitloss" },
  {
    label: "Hospital Services Ledger",
    path: "/admin/hospital/hospitalservicesledger",
  },
];

const CLOSING_LINKS = [
  { label: "By Day", path: "/admin/closing/month" },
  { label: "By Month", path: "/admin/closing/date" },
];

const PHARMACY_LINKS = [
  { label: "Purchase", path: "/admin/pharmacy/purchases" },
  { label: "Supplier Ledger", path: "/admin/pharmacy/ledger" },
  { label: "Distributors", path: "/admin/pharmacy/distributorList" },
  { label: "Stock", path: "/admin/pharmacy/stock" },
  { label: "Sales", path: "/admin/pharmacy/sale" },
  { label: "Sales Return", path: "/admin/pharmacy/sale/return" },
  {
    label: "Sales & Purchase Ledger",
    path: "/admin/pharmacy/pharmacyledger/salespurchaseledger",
  },
];

function NavItem({
  icon: Icon,
  label,
  isActive,
  expanded,
  onClick,
  hasSubmenu,
  submenuOpen,
}) {
  return (
    <div
      onClick={onClick}
      title={!expanded ? label : ""}
      className={[
        "relative flex items-center cursor-pointer rounded-xl overflow-hidden",
        "mx-2 my-[2px] px-[10px] py-[8px]",
        "border transition-all duration-200",
      ].join(" ")}
      style={
        isActive
          ? {
              background: "#E3ECF6",
              borderColor: "#BBD6F5",
              boxShadow: "0 2px 8px rgba(47,109,180,0.13)",
            }
          : {
              background: "transparent",
              borderColor: "transparent",
            }
      }
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.background = "#f8f9fa";
          e.currentTarget.style.borderColor = "#e5e7eb";
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.borderColor = "transparent";
        }
      }}
    >
      <div
        className="flex items-center justify-center w-[32px] h-[32px] rounded-[8px] flex-shrink-0 transition-all duration-200"
        style={
          isActive
            ? { background: "#2F6DB4" }
            : { background: "#f1f3f5", border: "1px solid #e5e7eb" }
        }
      >
        <Icon
          style={{
            fontSize: 17,
            color: isActive ? "#fff" : "#6b7280",
          }}
        />
      </div>

      <span
        className={[
          "ml-[10px] text-[13px] font-semibold whitespace-nowrap overflow-hidden transition-all duration-300 flex-1",
          expanded ? "max-w-[160px] opacity-100" : "max-w-0 opacity-0",
        ].join(" ")}
        style={{ color: isActive ? "#2F6DB4" : "#4b5563" }}
      >
        {label}
      </span>

      {hasSubmenu && expanded && (
        <IoIosArrowDown
          className="w-3 h-3 transition-transform duration-300 ml-auto"
          style={{
            color: "#9ca3af",
            transform: submenuOpen ? "rotate(-180deg)" : "rotate(0deg)",
          }}
        />
      )}

      {isActive && (
        <div
          className="absolute right-0 top-1/2 -translate-y-1/2 w-[3px] h-[55%] rounded-l"
          style={{ background: "#2F6DB4" }}
        />
      )}
    </div>
  );
}

const SubLinks = ({ links, navigate, location, onLinkClick }) => (
  <ul className="ml-[52px] mt-1 space-y-[2px] pr-3">
    {links.map(({ label, path }) => (
      <li
        key={path}
        onClick={() => {
          navigate(path);
          onLinkClick?.();
        }}
        className={`cursor-pointer text-xs font-semibold p-2 pl-3 rounded-md transition-colors
          ${
            location.pathname === path
              ? "text-white"
              : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"
          }`}
        style={
          location.pathname === path
            ? { background: "#2F6DB4" }
            : {}
        }
      >
        {label}
      </li>
    ))}
  </ul>
);

const IconPopup = ({
  icon: Icon,
  label,
  links,
  navigate,
  location,
  active,
}) => {
  const [show, setShow] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShow(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative flex justify-center w-full">
      <button
        onClick={() => setShow((s) => !s)}
        title={label}
        className="flex items-center justify-center w-[32px] h-[32px] rounded-[8px] transition-all duration-200"
        style={
          active
            ? { background: "#2F6DB4" }
            : { background: "#f1f3f5", border: "1px solid #e5e7eb" }
        }
      >
        <Icon style={{ fontSize: 17, color: active ? "#fff" : "#6b7280" }} />
      </button>

      {show && (
        <div
          className="fixed bg-white border border-gray-200 shadow-2xl rounded-2xl z-[99999] min-w-[190px] py-2 px-1"
          style={{
            left: 80,
            top: ref.current?.getBoundingClientRect().top ?? 0,
          }}
        >
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-3 pb-2">
            {label}
          </p>
          {links.map(({ label: lbl, path }) => (
            <div
              key={path}
              onClick={() => {
                navigate(path);
                setShow(false);
              }}
              className="cursor-pointer text-xs font-semibold px-3 py-2 rounded-xl transition-colors"
              style={
                location.pathname === path
                  ? { background: "#2F6DB4", color: "#fff" }
                  : { color: "#4b5563" }
              }
              onMouseEnter={(e) => {
                if (location.pathname !== path) {
                  e.currentTarget.style.background = "#f1f5f9";
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== path) {
                  e.currentTarget.style.background = "transparent";
                }
              }}
            >
              {lbl}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const sidebarStyle = {
  background: "#ffffff",
  boxShadow: "6px 0 32px rgba(0,0,0,0.10)",
};

const SidebarContent = ({ open, location, navigate, onLinkClick }) => {
  const [activeMenu, setActiveMenu] = useState(null);

  const isDash = location.pathname === "/admin";
  const isClosing = location.pathname.startsWith("/admin/closing");
  const isHospital = location.pathname.startsWith("/admin/hospital");
  const isPharmacy = location.pathname.startsWith("/admin/pharmacy");
  const isDutyRoster = location.pathname.startsWith("/admin/duty-roster");

  useEffect(() => {
    if (location.pathname.startsWith("/admin/hospital")) {
      setActiveMenu("hospital");
    } else if (location.pathname.startsWith("/admin/pharmacy")) {
      setActiveMenu("pharmacy");
    } else if (location.pathname.startsWith("/admin/closing")) {
      setActiveMenu("closing");
    }
  }, [location.pathname]);

  if (!open) {
    return (
      <div className="flex flex-col items-center h-full overflow-y-auto overflow-x-visible pt-5 gap-3 pb-4">
        <div className="flex items-center justify-center pb-4 border-b border-gray-100 w-full mb-1">
          <img src={logo} alt="logo" className="w-8 object-contain" />
        </div>

        <button
          onClick={() => navigate("/admin")}
          title="Dashboard"
          className="flex items-center justify-center w-[32px] h-[32px] rounded-[8px] transition-all duration-200"
          style={
            isDash
              ? { background: "#2F6DB4" }
              : { background: "#f1f3f5", border: "1px solid #e5e7eb" }
          }
        >
          <TbLayoutDashboardFilled
            style={{ fontSize: 17, color: isDash ? "#fff" : "#6b7280" }}
          />
        </button>

        <IconPopup
          icon={PiHospitalFill}
          label="Closing"
          links={CLOSING_LINKS}
          navigate={navigate}
          location={location}
          active={isClosing}
        />
        <IconPopup
          icon={PiHospitalFill}
          label="Hospital"
          links={HOSPITAL_LINKS}
          navigate={navigate}
          location={location}
          active={isHospital}
        />
        <IconPopup
          icon={FaStore}
          label="Pharmacy"
          links={PHARMACY_LINKS}
          navigate={navigate}
          location={location}
          active={isPharmacy}
        />

        <button
          onClick={() => navigate("/admin/duty-roster")}
          title="Duty Roster"
          className="flex items-center justify-center w-[32px] h-[32px] rounded-[8px] transition-all duration-200"
          style={
            isDutyRoster
              ? { background: "#2F6DB4" }
              : { background: "#f1f3f5", border: "1px solid #e5e7eb" }
          }
        >
          <MdOutlineCalendarMonth
            style={{ fontSize: 17, color: isDutyRoster ? "#fff" : "#6b7280" }}
          />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto py-2">
      <div className="relative flex items-center justify-center px-3 border-b border-gray-100 mb-2">
        <img
          onClick={() => {
            navigate("/admin");
            onLinkClick?.();
          }}
          src={logo}
          alt="logo"
          className="object-contain transition-all duration-300 w-48 h-48 cursor-pointer"
        />
      </div>

      <p className="text-[10px] font-bold tracking-[1.2px] uppercase pt-2 text-gray-400 px-5 pb-1 m-0">
        Navigation
      </p>

      <nav className="relative py-1 flex-1">
        <NavItem
          icon={TbLayoutDashboardFilled}
          label="Dashboard"
          isActive={isDash}
          expanded={open}
          onClick={() => {
            navigate("/admin");
            onLinkClick?.();
          }}
        />

        <NavItem
          icon={PiHospitalFill}
          label="Closing"
          isActive={isClosing}
          expanded={open}
          onClick={() =>
            setActiveMenu(activeMenu === "closing" ? null : "closing")
          }
          hasSubmenu
          submenuOpen={activeMenu === "closing"}
        />
        {activeMenu === "closing" && (
          <SubLinks
            links={CLOSING_LINKS}
            navigate={navigate}
            location={location}
            onLinkClick={onLinkClick}
          />
        )}

        <NavItem
          icon={PiHospitalFill}
          label="Hospital"
          isActive={isHospital}
          expanded={open}
          onClick={() =>
            setActiveMenu(activeMenu === "hospital" ? null : "hospital")
          }
          hasSubmenu
          submenuOpen={activeMenu === "hospital"}
        />
        {activeMenu === "hospital" && (
          <SubLinks
            links={HOSPITAL_LINKS}
            navigate={navigate}
            location={location}
            onLinkClick={onLinkClick}
          />
        )}

        <NavItem
          icon={FaStore}
          label="Pharmacy"
          isActive={isPharmacy}
          expanded={open}
          onClick={() =>
            setActiveMenu(activeMenu === "pharmacy" ? null : "pharmacy")
          }
          hasSubmenu
          submenuOpen={activeMenu === "pharmacy"}
        />
        {activeMenu === "pharmacy" && (
          <SubLinks
            links={PHARMACY_LINKS}
            navigate={navigate}
            location={location}
            onLinkClick={onLinkClick}
          />
        )}

        <NavItem
          icon={MdOutlineCalendarMonth}
          label="Duty Roster"
          isActive={isDutyRoster}
          expanded={open}
          onClick={() => {
            navigate("/admin/duty-roster");
            onLinkClick?.();
          }}
        />
      </nav>
    </div>
  );
};

export default function PortalLayout({ children }) {
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [userEmail, setUserEmail] = useState("");

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const email = sessionStorage.getItem("userEmail");
    if (email) setUserEmail(email);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("user");
    Cookies.remove("Q1JFVC1LRVkXIOQUNVU1RPTUVSLUFVVEhFTlRJQ0FUSU9OIMSL=");
    toast.success("Logout Successfully");
    navigate("/login", { replace: true });
  };

  const getPageTitle = () => {
    if (location.pathname === "/admin") return "Dashboard";
    if (location.pathname.startsWith("/admin/hospital")) return "Hospital";
    if (location.pathname.startsWith("/admin/pharmacy")) return "Pharmacy";
    if (location.pathname.startsWith("/admin/closing")) return "Closing";
    if (location.pathname.startsWith("/admin/duty-roster")) return "Duty Roster";
    return "Admin Portal";
  };

  if (location.pathname === "/login") return children;

  return (
    <div className="flex h-screen overflow-hidden bg-slate-100">
      <CssBaseline />

      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[9998] lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div
        className={`fixed top-0 left-0 h-full z-[9999] shadow-2xl
          transition-transform duration-300 ease-in-out lg:hidden
          rounded-tr-[24px] rounded-br-[24px] overflow-hidden
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ width: drawerWidth, ...sidebarStyle }}
      >
        <button
          onClick={() => setMobileOpen(false)}
          className="absolute top-3 right-3 p-1 rounded-full text-gray-400 hover:bg-gray-100 z-10"
        >
          <CloseIcon fontSize="small" />
        </button>
        <SidebarContent
          open={true}
          location={location}
          navigate={navigate}
          onLinkClick={() => setMobileOpen(false)}
        />
      </div>

      <div
        className="hidden lg:block flex-shrink-0 h-full rounded-tr-[12px] rounded-br-[12px] transition-all duration-300 ease-in-out overflow-visible relative"
        style={{ width: desktopOpen ? drawerWidth : 72, ...sidebarStyle }}
      >
        <div className="relative h-full">
          <SidebarContent
            open={desktopOpen}
            location={location}
            navigate={navigate}
          />
        </div>
      </div>

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <header className="relative z-30 flex-shrink-0 m-2 sm:m-3">
          <div className="rounded-2xl border border-white/70 bg-white/85 backdrop-blur-xl shadow-[0_4px_24px_rgba(0,0,0,0.08)]">
            <div className="flex items-center px-3 sm:px-4 h-14 sm:h-[56px]">
              <IconButton
                onClick={() => {
                  if (window.innerWidth >= 1024) setDesktopOpen((v) => !v);
                  else setMobileOpen((v) => !v);
                }}
                edge="start"
                className="!mr-2 !w-9 !h-9 !rounded-xl !bg-gray-700/[0.07] hover:!bg-gray-700/[0.14] !transition-colors"
              >
                <MenuIcon className="!text-[20px] !text-gray-700" />
              </IconButton>

              <div className="ml-2">
                <p className="text-[15px] font-bold text-gray-700 m-0 leading-tight">
                  {getPageTitle()}
                </p>
                <p className="text-[11px] text-gray-400 m-0">
                  {new Date().toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "short",
                    day: "numeric",
                  })}
                </p>
              </div>

              <div className="flex-1" />

              <div className="flex items-center gap-3">
                <Popover className="relative">
                  <Popover.Button
                    as="div"
                    onClick={() => setIsProfileOpen((v) => !v)}
                    className="flex items-center gap-2 px-3 py-[6px] rounded-full cursor-pointer select-none
                      bg-gradient-to-r from-[#2F6DB4] via-[#5894d9] to-white/90
                      shadow-[0_2px_12px_rgba(0,0,0,0.2)]
                      hover:shadow-[0_4px_20px_rgba(0,0,0,0.3)] transition-shadow duration-200"
                  >
                    <AccountCircleIcon className="!text-[20px] !text-white/90" />
                    {userEmail && (
                      <span className="hidden sm:block text-xs text-white/70 truncate max-w-[120px]">
                        {userEmail}
                      </span>
                    )}
                    <IoIosArrowDown
                      className="w-3.5 h-3.5 text-white/70 transition-transform duration-300"
                      style={{
                        transform: isProfileOpen
                          ? "rotate(-180deg)"
                          : "rotate(0deg)",
                      }}
                    />
                  </Popover.Button>

                  <Transition
                    as={React.Fragment}
                    enter="transition ease-out duration-150"
                    enterFrom="opacity-0 -translate-y-2"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 -translate-y-2"
                  >
                    <Popover.Panel
                      className="absolute top-[calc(100%+10px)] right-0 z-[9999]
                      min-w-[220px] rounded-2xl overflow-hidden
                      bg-white/[0.97] backdrop-blur-xl
                      border border-gray-700/10
                      shadow-[0_20px_60px_rgba(0,0,0,0.15)]"
                    >
                      <div className="flex items-center gap-2.5 p-4 bg-gradient-to-r from-[#2F6DB4] via-[#5894d9] to-white/90">
                        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                          <FaRegUser className="text-white text-base" />
                        </div>
                        <div>
                          {userEmail && (
                            <p className="text-white text-[12px] font-semibold m-0 truncate max-w-[130px]">
                              {userEmail}
                            </p>
                          )}
                          <p className="text-white/60 text-[11px] m-0">
                            Admin
                          </p>
                        </div>
                      </div>

                      <div
                        onClick={handleLogout}
                        className="flex items-center gap-2.5 m-2.5 px-3.5 py-2.5 rounded-xl cursor-pointer
                          bg-gradient-to-r from-red-500 to-red-600
                          text-white font-semibold text-[13px]
                          shadow-[0_3px_12px_rgba(239,68,68,0.3)]
                          hover:-translate-y-px hover:shadow-[0_6px_18px_rgba(239,68,68,0.4)]
                          transition-all duration-200"
                      >
                        <PowerSettingsNewIcon className="!text-[18px]" />
                        <span>Sign Out</span>
                      </div>
                    </Popover.Panel>
                  </Transition>
                </Popover>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-hidden">
          <div className="h-full bg-gradient-to-br from-gray-50 to-gray-100 p-3 sm:p-4 rounded-2xl m-2 sm:m-3 mt-0 shadow-[0_2px_16px_rgba(0,0,0,0.06)]">
            <div className="h-full overflow-y-auto no-scrollbar">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}